import { useState, FC, useEffect } from 'react';
import { useTranslation } from '@wix/fe-essentials-standalone/i18n';
import _ from 'lodash';
import { useShallowRouter } from '@components/Hooks/useRouter';
import { useTemplatesContext } from '@components/Contexts/TemplatesContext';
import { updateQueryParams } from '@utils/route';
import MobileFilterModalLayout from '@components/MobileFilter/MobileFilterModalLayout';
import {
  FILTER_TO_KEY_PREFIX_MAP,
  SingleSelectionFilters,
} from '@components/Templates/TemplateGallery/FilterTags/useFilterTags';
import ControlledRadioGroup from '@components/Templates/FilterPanel/OneSelectionFilter/ControlledRadioGroup';
import { getSingleSelectionFilterCount } from '@components/Hooks/useTemplateGalleryFilters/useTemplateGalleryFilters';
import { IClearAllRecord, IOnClear } from '../TemplateMobileFilters';
import { FILTER_TO_TITLE_MAP } from '../TemplatesMobileFiltersMenu/TemplatesMobileFiltersMenu';
import { useTemplatesBi } from '@components/Hooks/useTemplatesBi';
import { useFiltersShouldClearSearch } from '@components/Hooks/useFiltersShouldClearSearch';

export const dataHooks = {
  filterScreen: (filterType: SingleSelectionFilters) =>
    `single-selection-filter-screen-${filterType}`,
};

interface ISingleSelectionFilterScreenProps {
  onBack: () => void;
  onClose: () => void;
  items: Record<string, string>;
  filterType: SingleSelectionFilters;
  onClear: IOnClear;
  clearAllRecord: IClearAllRecord;
}

const ALL_OPTION = 'ALL';

const SingleSelectionFilterScreen: FC<ISingleSelectionFilterScreenProps> = ({
  onBack,
  onClose,
  items,
  filterType,
  onClear,
  clearAllRecord,
}) => {
  const { t } = useTranslation();
  const { navigate, router } = useShallowRouter();
  const {
    state: { filters },
  } = useTemplatesContext();
  const { logBiClick } = useTemplatesBi({ is_mobile: true });
  const shouldClearSearch = useFiltersShouldClearSearch();

  const stateValue = filters[filterType];
  const wasCleared = clearAllRecord[filterType] || clearAllRecord.all;
  const initValue = wasCleared ? ALL_OPTION : stateValue;

  const [selected, setSelected] = useState<keyof typeof items | undefined>(
    initValue,
  );

  const filterCount = getSingleSelectionFilterCount(selected);

  const onApply = () => {
    const selectedItem =
      selected === ALL_OPTION ? undefined : (selected as keyof typeof items);

    logBiClick({
      click_type: `${filterType} filter - Apply`,
      click_data: { filter_list: [selectedItem] },
    });

    navigate(
      updateQueryParams({ [filterType]: selectedItem }, router, {
        search: shouldClearSearch,
        ...clearAllRecord,
      }),
    );
    onClose();
  };

  const onClearClick = () => {
    onClear(filterType);
    setSelected(ALL_OPTION);
  };

  useEffect(() => {
    setSelected(initValue);
  }, [initValue]);

  const onChange = (val?: string | number) => {
    logBiClick({
      click_type: 'mark filter',
      click_data: { filter_name: `${filterType} - ${val}` },
    });
    setSelected(val as keyof typeof items);
  };

  return (
    <MobileFilterModalLayout
      title={t(FILTER_TO_TITLE_MAP[filterType])}
      selectedAmount={filterCount}
      onClear={onClearClick}
      onApply={onApply}
      onBack={onBack}
      dataHook={dataHooks.filterScreen(filterType)}
      content={
        <div
          style={{
            padding: '12px 24px 12px 24px',
          }}
        >
          <ControlledRadioGroup
            items={items}
            selected={selected}
            onChange={onChange}
            langTokenPrefix={FILTER_TO_KEY_PREFIX_MAP[filterType]}
            spacing="24px"
          />
        </div>
      }
    />
  );
};

export default SingleSelectionFilterScreen;
